import APITemplate from '@api/APITemplate'
import con from '@services/connection'
import store from '@state/store'
import { intelligenceV2 } from '@modules/aawz-intelligence-v2/constants/URL'
import { podeConectarWhatsapp } from '@modules/aawz-intelligence-v2/utils/whatsapp-ia-enabled'

const NS = 'aawz-intelligence-v2'
const SEM_VINCULO = { status: 'nenhum' }

// Vínculo WhatsApp ↔ conta do profissional (canal WhatsApp da IA).
// Contrato HTTP 200-always com `status` no corpo (sessão, connection.js).
class APIWhatsappVinculo extends APITemplate {
  // A API faz a rede E grava na store (setter puro `setWhatsappVinculo`); as
  // telas leem `whatsappConectado`/`whatsappVinculo` pelos getters. Toda
  // resposta que traz o vínculo (obter, gerar, revogar, preferências) passa
  // aqui, então a store nunca fica atrás do modal.
  _gravar(r) {
    if (r && r.status === 200 && r.value && r.value.status) {
      store.dispatch(NS + '/setWhatsappVinculo', r.value)
    }
    return r
  }

  // Garante o vínculo na store UMA vez por sessão (`forcar` relê). Fora do
  // gate (escritório/usuário sem a feature) não chama a API: grava 'nenhum' —
  // é o mesmo gate do item de menu, então quem não vê "Conectar WhatsApp" não
  // tem vínculo que interesse à tela. Erro de rede também vira 'nenhum', para
  // a tela nunca ficar "não apurada".
  async garantir({ user, access, forcar = false } = {}) {
    const atual = store.getters[NS + '/whatsappVinculo'] || {}
    if (atual.carregado && !forcar) return atual
    if (!podeConectarWhatsapp({ user, access })) {
      store.dispatch(NS + '/setWhatsappVinculo', SEM_VINCULO)
      return store.getters[NS + '/whatsappVinculo']
    }
    const r = await this.obter()
    if (!(r && r.status === 200 && r.value && r.value.status)) {
      store.dispatch(NS + '/setWhatsappVinculo', SEM_VINCULO)
    }
    return store.getters[NS + '/whatsappVinculo']
  }

  // Estado atual: { status: 'nenhum' | 'pendente' | 'ativo', codigo?, expirado?,
  // link_wa?, numero_ia?, numero_mascarado?, verificado_em?, escrita_habilitada? }
  async obter() {
    const r = await con.get(intelligenceV2.whatsappVinculo)
    return this._gravar(r) || this.buildErrorResponse({ message: 'Erro inesperado' })
  }

  // Gera (ou renova) o código pendente. 409 = já conectado; 429 = rate limit.
  async gerarCodigo() {
    const r = await con.post(intelligenceV2.whatsappVinculo, {})
    return this._gravar(r) || this.buildErrorResponse({ message: 'Erro inesperado' })
  }

  async revogar() {
    const r = await con.delet(intelligenceV2.whatsappVinculo)
    return this._gravar(r) || this.buildErrorResponse({ message: 'Erro inesperado' })
  }

  // Toggle "permitir que a IA altere dados pelo WhatsApp" (sempre com confirmação
  // por *sim* no próprio WhatsApp). 409 = sem número conectado.
  // Preferências do vínculo (corpo plano, uma ou mais chaves booleanas:
  // escrita_habilitada, aviso_tarefas_hoje…). 400 = chave desconhecida ou valor
  // não booleano (`errors` por chave); 409 = sem número conectado.
  // `json: true`: o resource lê `request.get_json()` — o FormData padrão do
  // `con` chegaria vazio e o backend responderia 400 (achado no QA visual).
  async definirPreferencias(valores) {
    const corpo = {}
    Object.keys(valores || {}).forEach((chave) => {
      corpo[chave] = Boolean(valores[chave])
    })
    const r = await con.patch(intelligenceV2.whatsappVinculo, corpo, false, {
      json: true,
    })
    return this._gravar(r) || this.buildErrorResponse({ message: 'Erro inesperado' })
  }

  definirEscrita(habilitada) {
    return this.definirPreferencias({ escrita_habilitada: habilitada })
  }

  // Reenvia um PDF já gerado ao WhatsApp do PRÓPRIO profissional.
  // 200 enviado · 404 PDF ainda em geração · 409 sem número · 403 canal fechado.
  async enviarPdf({ idCliente, tipo, hash, type }) {
    const corpo = { tipo, hash }
    if (type) corpo.type = type
    const r = await con.post(
      intelligenceV2.whatsappEnviarPdf + idCliente,
      corpo,
      false,
      { json: true }
    )
    return r || this.buildErrorResponse({ message: 'Erro inesperado' })
  }

  // Botão "Iniciar onboarding pelo WhatsApp": o backend encerra o que estava
  // rolando no WhatsApp e manda a primeira pergunta. Sem corpo. 403 = sem a flag
  // do escritório; 409 = sem número conectado, já realizado ou IA ainda
  // respondendo (`message` vem do backend). `value` é o estado do vínculo.
  async iniciarOnboarding() {
    const r = await con.post(intelligenceV2.whatsappOnboarding, {}, false, {
      json: true,
    })
    return r || this.buildErrorResponse({ message: 'Erro inesperado' })
  }
}

export const apiWhatsappVinculo = new APIWhatsappVinculo()
